import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getMission } from "../../services/missionService";
import { getWeightEntriesForPerson } from "../../services/personService";
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export default function MissionDetail() {
  const { id } = useParams();
  const [mission, setMission] = useState(null);
  const [weightData, setWeightData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartBackground, setChartBackground] = useState("none");
  const [yAxisDomain, setYAxisDomain] = useState([0, 100]);

  useEffect(() => {
    async function loadData() {
      try {
        const missionData = await getMission(id);
        setMission(missionData);

        // Load weight entries if it's a LoseWeightMission
        if (missionData.type === "LoseWeight") {
          console.log("\n=== LoseWeight Mission Processing Start ===");
          console.log("Raw mission data from API:", missionData);
          
          // Ensure weights are numbers
          const startWeight = parseFloat(missionData.startWeight);
          const targetWeight = parseFloat(missionData.targetWeight);
          
          console.log("After parseFloat:", {
            startWeightRaw: missionData.startWeight,
            startWeightParsed: startWeight,
            targetWeightRaw: missionData.targetWeight,
            targetWeightParsed: targetWeight,
            startWeightIsNaN: isNaN(startWeight),
            targetWeightIsNaN: isNaN(targetWeight),
          });
          
          // Check if weights are valid
          if (isNaN(startWeight) || isNaN(targetWeight)) {
            console.error("Invalid weights:", { startWeight, targetWeight, missionData });
            setError("Gewichten zijn niet correct gedefinieerd in de missie");
            setLoading(false);
            return;
          }
          
          const weightEntries = await getWeightEntriesForPerson(missionData.personId);
          
          // Parse and prepare data for the chart
          const dataPoints = [];
          let allWeights = [startWeight, targetWeight];
          
          // Add start weight
          dataPoints.push({
            date: missionData.startDate,
            daysSinceStart: 0,
            actualWeight: startWeight,
            intermediateTargetWeight: parseFloat(startWeight.toFixed(2)),
            type: "start",
          });

          // Helper function to parse date formats (dd/mm/yyyy or yyyy-mm-dd)
          const parseDate = (dateString) => {
            if (!dateString || typeof dateString !== 'string') {
              console.error("Invalid dateString:", dateString);
              return new Date(NaN);
            }
            
            let day, month, year;
            
            // Check if it's ISO format (yyyy-mm-dd)
            if (dateString.includes('-')) {
              console.log("Detected ISO format:", dateString);
              const parts = dateString.split('-');
              if (parts.length !== 3) {
                console.error("Unexpected ISO date format:", dateString);
                return new Date(NaN);
              }
              year = parseInt(parts[0], 10);
              month = parseInt(parts[1], 10);
              day = parseInt(parts[2], 10);
            }
            // Check if it's Dutch format (dd/mm/yyyy)
            else if (dateString.includes('/')) {
              console.log("Detected Dutch format:", dateString);
              const parts = dateString.split('/');
              if (parts.length !== 3) {
                console.error("Unexpected date format:", dateString);
                return new Date(NaN);
              }
              day = parseInt(parts[0], 10);
              month = parseInt(parts[1], 10);
              year = parseInt(parts[2], 10);
            } else {
              console.error("Unknown date format:", dateString);
              return new Date(NaN);
            }
            
            console.log("Parsed date parts:", { dateString, day, month, year });
            
            // JavaScript Date months are 0-indexed, so subtract 1 from month
            const date = new Date(year, month - 1, day);
            
            console.log("Parsed date result:", { dateString, date, dateTime: date.getTime(), isValid: !isNaN(date.getTime()) });
            return date;
          };

          // Helper function to calculate days between two dates
          const calculateTotalTime = (startDate, targetDate) => {
            console.log("calculateTotalTime inputs:", { startDate, targetDate, startDateType: typeof startDate, targetDateType: typeof targetDate });
            
            const start = parseDate(startDate);
            const target = parseDate(targetDate);
            
            console.log("After parseDate():", {
              startRaw: start,
              startTime: start.getTime(),
              startTimeIsNaN: isNaN(start.getTime()),
              targetRaw: target,
              targetTime: target.getTime(),
              targetTimeIsNaN: isNaN(target.getTime()),
            });
            
            const totalTimeMission = target.getTime() - start.getTime();
            const days = totalTimeMission / (1000 * 60 * 60 * 24);
            console.log("DEBUG - calculateTotalTime:", {
              startDate,
              targetDate,
              startTime: start.getTime(),
              targetTime: target.getTime(),
              diffMs: totalTimeMission,
              days: days,
              isValid: !isNaN(days) && days > 0,
            });
            return days;
          };

          const calculateElapsedTime = (startDate, entryDate) => {
            const start = parseDate(startDate);
            const entry = parseDate(entryDate);
            const elapsedTime = entry.getTime() - start.getTime();
            const days = elapsedTime / (1000 * 60 * 60 * 24);
            console.log("DEBUG - calculateElapsedTime:", {
              startDate,
              entryDate,
              startTime: start.getTime(),
              entryTime: entry.getTime(),
              diffMs: elapsedTime,
              days: days,
              isValid: !isNaN(days),
            });
            return days;
          };

          const totalTimeMission = calculateTotalTime(missionData.startDate, missionData.targetDate);
          
          console.log("DEBUG - Mission Data:", {
            startWeight: missionData.startWeight,
            startWeightType: typeof missionData.startWeight,
            targetWeight: missionData.targetWeight,
            targetWeightType: typeof missionData.targetWeight,
            startDate: missionData.startDate,
            targetDate: missionData.targetDate,
            totalTimeMission: totalTimeMission,
            totalTimeMissionIsNaN: isNaN(totalTimeMission),
            totalTimeMissionIsPositive: totalTimeMission > 0,
          });

          // Add weight entries sorted by date
          if (weightEntries && weightEntries.length > 0) {
            weightEntries
              .sort((a, b) => new Date(a.weighingDate) - new Date(b.weighingDate))
              .forEach((entry, index) => {
                console.log(`\n--- Weight Entry ${index + 1} ---`);
                console.log("Raw entry data:", entry);
                
                // Calculate intermediate target weight for this date
                const elapsedTime = calculateElapsedTime(missionData.startDate, entry.weighingDate);
                const percentageElapsed = Math.min(elapsedTime / totalTimeMission, 1);
                
                console.log("Calculation step 1 - elapsedTime:", {
                  elapsedTime,
                  isNaN: isNaN(elapsedTime),
                });
                
                console.log("Calculation step 2 - percentageElapsed:", {
                  percentageElapsed,
                  totalTimeMission,
                  isNaN: isNaN(percentageElapsed),
                  division: elapsedTime / totalTimeMission,
                });
                
                const weightDifference = startWeight - targetWeight;
                const weightAdjustment = weightDifference * percentageElapsed;
                const intermediateTarget = parseFloat((startWeight - weightAdjustment).toFixed(2));
                
                console.log("Calculation step 3 - intermediateTarget:", {
                  startWeight,
                  targetWeight,
                  weightDifference,
                  percentageElapsed,
                  weightAdjustment,
                  intermediateTarget,
                  isNaN: isNaN(intermediateTarget),
                });
                
                console.log("DEBUG - Entry calculation:", {
                  weighingDate: entry.weighingDate,
                  elapsedTime: elapsedTime,
                  percentageElapsed: percentageElapsed,
                  intermediateTarget: intermediateTarget,
                });

                allWeights.push(entry.weightInKilograms);

                dataPoints.push({
                  date: entry.weighingDate,
                  daysSinceStart: elapsedTime,
                  actualWeight: entry.weightInKilograms,
                  intermediateTargetWeight: intermediateTarget,
                  type: "entry",
                });
              });
          }

          // Add target weight
          dataPoints.push({
            date: missionData.targetDate,
            daysSinceStart: totalTimeMission,
            actualWeight: targetWeight,
            intermediateTargetWeight: null,
            type: "target",
          });

          setWeightData(dataPoints);
          
          // Calculate Y-axis domain based on all weights
          const minWeight = Math.min(...allWeights);
          const maxWeight = Math.max(...allWeights);
          setYAxisDomain([minWeight - 1, maxWeight + 1]);

          // Determine background color based on latest weight vs target
          if (weightEntries && weightEntries.length > 0) {
            const latestEntry = weightEntries[weightEntries.length - 1];
            const elapsedTime = calculateElapsedTime(missionData.startDate, latestEntry.weighingDate);
            const percentageElapsed = Math.min(elapsedTime / totalTimeMission, 1);
            const expectedWeight =
              startWeight -
              ((startWeight - targetWeight) * percentageElapsed);

            if (latestEntry.weightInKilograms > expectedWeight) {
              setChartBackground("rgba(255, 165, 0, 0.1)"); // Light orange
            } else {
              setChartBackground("rgba(144, 238, 144, 0.1)"); // Light green
            }
          }
        }
      } catch (err) {
        setError("Fout bij het laden van missie: " + err.message);
        console.error("Error loading mission detail:", err);
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      loadData();
    }
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!mission) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Missie niet gevonden
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Missie{mission.name && ` - ${mission.name}`}
      </Typography>

      {/* Gegevens Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Gegevens
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Type
          </Typography>
          <Typography variant="body2">{mission.type}</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Status
          </Typography>
          <Typography variant="body2">{mission.status}</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Beschrijving
          </Typography>
          <Typography variant="body2">{mission.description || "-"}</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Startdatum
          </Typography>
          <Typography variant="body2">{mission.startDate}</Typography>

          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            Doeldatum
          </Typography>
          <Typography variant="body2">{mission.targetDate}</Typography>

          {mission.type === "LoseWeight" && (
            <>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Startgewicht
              </Typography>
              <Typography variant="body2">
                {mission.startWeight ?? "-"} kg
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Doelgewicht
              </Typography>
              <Typography variant="body2">
                {mission.targetWeight ?? "-"} kg
              </Typography>

              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Voortgang
              </Typography>
              <Typography variant="body2">
                {mission.percentageCompleted?.toFixed(1) ?? "-"}%
              </Typography>
            </>
          )}
        </Box>
      </Paper>

      {/* Weight Tracking Chart for LoseWeightMission */}
      {mission.type === "LoseWeight" && weightData.length > 0 && (
        <Paper
          sx={{
            p: 3,
            backgroundColor: chartBackground,
            transition: "background-color 0.3s ease",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Gewichtsinformatie
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={weightData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="daysSinceStart"
                type="number"
                label={{ value: "Dagen sinds start", position: "insideBottomRight", offset: -5 }}
                tickFormatter={(days) => `${Math.round(days)}d`}
              />
              <YAxis 
                domain={yAxisDomain}
                label={{ value: "Gewicht (kg)", angle: -90, position: "insideLeft" }} 
              />
              <Tooltip
                formatter={(value) => {
                  if (typeof value === 'number') {
                    return `${value.toFixed(2)} kg`;
                  }
                  return value;
                }}
                labelFormatter={(label) => {
                  if (typeof label === 'number') {
                    return `Dag ${Math.round(label)}`;
                  }
                  return label;
                }}
                content={({ active, payload }) => {
                  if (active && payload && payload[0]) {
                    const data = payload[0].payload;
                    return (
                      <div style={{ backgroundColor: 'white', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
                        <p style={{ margin: '0 0 4px 0' }}>Datum: {data.date}</p>
                        <p style={{ margin: '0 0 4px 0' }}>Dag: {Math.round(data.daysSinceStart)}</p>
                        {payload.map((entry, index) => (
                          <p key={index} style={{ margin: '0', color: entry.color }}>
                            {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value} kg
                          </p>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />

              {/* Intermediate target line (red with red dots) */}
              <Line
                type="monotone"
                dataKey="intermediateTargetWeight"
                stroke="red"
                strokeWidth={2}
                name="Tussendoelgewicht"
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (payload.type === "start" || payload.type === "target") {
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={5}
                        fill="red"
                        stroke="darkred"
                        strokeWidth={2}
                      />
                    );
                  } else if (payload.type === "entry") {
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={4}
                        fill="red"
                        stroke="darkred"
                        strokeWidth={1}
                      />
                    );
                  }
                  return null;
                }}
                isAnimationActive={false}
              />

              {/* Progress line (black, connecting start and weight entries) */}
              <Line
                type="monotone"
                dataKey="actualWeight"
                stroke="black"
                name="Werkelijk gewicht"
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (payload.type === "start" || payload.type === "target") {
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={6}
                        fill="black"
                        stroke="darkgray"
                        strokeWidth={2}
                      />
                    );
                  } else if (payload.type === "entry") {
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={4}
                        fill="black"
                        stroke="black"
                        strokeWidth={1}
                      />
                    );
                  }
                  return null;
                }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}
    </Box>
  );
}
